import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '@services/authService';
import { Admin } from '@services/core/types';

interface AuthContextType {
  admin: Admin | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!admin;

  useEffect(() => {
    const initializeAuth = async () => {
      const token = authService.getToken();
      if (token) {
        try {
          const adminData = await authService.getProfile();
          setAdmin(adminData);
        } catch (error) {
          console.error('Failed to verify token with backend:', error);
          authService.logout();
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    try {
      const response = await authService.login(username, password);
      authService.setAuth(response.token, response.admin);
      setAdmin(response.admin);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setAdmin(null);
    authService.logout();
    window.location.href = '/login';
  };

  const value: AuthContextType = {
    admin,
    isAuthenticated,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};